import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Film, Plus, Loader2, Play, Pause, Trash2, Check, 
  Clock, Video, ChevronDown, ChevronUp, Copy, ExternalLink,
  Share2, Download, SkipBack, SkipForward, Volume2, VolumeX,
  Scissors, Eye, Twitter, Facebook, Link2
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';
const API = `${BACKEND_URL}/api`;

// Stat type icons
const STAT_ICONS = {
  goal: '⚽', try: '🏈', basket: '🏀', save: '🛡️', 
  tackle: '💪', assist: '🎯', other: '📝'
};

export default function HighlightReel({ childId, videos = [] }) {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreator, setShowCreator] = useState(false);
  const [expandedReel, setExpandedReel] = useState(null);
  const [previewClip, setPreviewClip] = useState(null);
  const [showShareMenu, setShowShareMenu] = useState(null);
  
  // Video player state
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  
  // Creator state
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [reelTitle, setReelTitle] = useState('');

  const loadReels = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/training-plan/highlight-reels/${childId}`);
      setReels(response.data.reels || []);
    } catch (error) {
      // May not have any reels yet
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    loadReels();
  }, [loadReels]);

  const handleToggleVideo = (videoId) => {
    if (selectedVideos.includes(videoId)) {
      setSelectedVideos(selectedVideos.filter(id => id !== videoId));
    } else {
      setSelectedVideos([...selectedVideos, videoId]);
    }
  };

  const handleCreateReel = async () => {
    if (selectedVideos.length === 0) {
      toast.error('Select at least one video');
      return;
    }

    if (!reelTitle.trim()) {
      toast.error('Enter a title for the highlight reel');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/training-plan/highlight-reel/${childId}`, {
        video_ids: selectedVideos,
        title: reelTitle,
        include_all_markers: true
      });
      
      toast.success(`Created "${reelTitle}" with ${response.data.total_clips} clips!`);
      setShowCreator(false);
      setSelectedVideos([]);
      setReelTitle('');
      loadReels();
    } catch (error) {
      toast.error('Failed to create highlight reel');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteReel = async (reelId) => {
    if (!window.confirm('Delete this highlight reel?')) return;
    
    try {
      await axios.delete(`${API}/training-plan/highlight-reel/${reelId}`);
      toast.success('Highlight reel deleted');
      loadReels();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Video player controls
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSkip = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime += seconds;
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const rect = e.target.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * duration;
    }
  };

  // Preview clip
  const handlePreviewClip = (clip, videoUrl) => {
    setPreviewClip({ ...clip, videoUrl });
    setIsPlaying(false);
  };

  // Social sharing
  const handleShare = async (reel, platform) => {
    const shareUrl = `${window.location.origin}/highlights/${reel.id}`;
    const shareText = `Check out "${reel.title}" - ${reel.total_clips} amazing moments!`;
    
    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'copy':
        try {
          await navigator.clipboard.writeText(shareUrl);
          toast.success('Link copied to clipboard!');
        } catch {
          toast.error('Failed to copy link');
        }
        break;
      default:
        break;
    }
    setShowShareMenu(null);
  };

  // Filter videos that have timestamp markers
  const videosWithMarkers = videos.filter(v => v.timestamp_markers?.length > 0);

  if (loading) {
    return (
      <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 text-[#02b5ff] animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950/30 border border-zinc-800 backdrop-blur-sm p-6" data-testid="highlight-reel">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Film className="h-6 w-6 text-yellow-400" />
          <div>
            <h2 className="text-lg font-bold uppercase tracking-widest text-white">Highlight Reels</h2>
            <p className="text-xs text-zinc-500">{reels.length} compilation{reels.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <Button
          onClick={() => setShowCreator(!showCreator)}
          disabled={videosWithMarkers.length === 0}
          className="h-9 px-4 bg-yellow-600 hover:bg-yellow-500 text-white text-xs disabled:opacity-50"
          data-testid="create-reel-button"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Reel
        </Button>
      </div>

      {/* Creator Panel */}
      {showCreator && (
        <div className="mb-6 p-4 bg-zinc-900/50 border border-zinc-700 rounded-lg">
          <div className="mb-4">
            <label className="block text-xs text-zinc-500 mb-2 uppercase">Reel Title</label>
            <Input
              type="text"
              placeholder="e.g., Season Highlights, Best Goals"
              value={reelTitle}
              onChange={(e) => setReelTitle(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white text-sm"
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-xs text-zinc-500 mb-2 uppercase">
              Select Videos ({selectedVideos.length} selected)
            </label>
            {videosWithMarkers.length === 0 ? (
              <p className="text-sm text-zinc-500 p-3 bg-zinc-800/50 rounded">
                No videos with timestamp markers. Add markers to videos first.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {videosWithMarkers.map((video) => {
                  const isSelected = selectedVideos.includes(video.id);
                  return (
                    <div
                      key={video.id}
                      onClick={() => handleToggleVideo(video.id)}
                      className={`flex items-center gap-3 p-3 rounded cursor-pointer transition-colors ${
                        isSelected 
                          ? 'bg-yellow-600/20 border border-yellow-500' 
                          : 'bg-zinc-800/50 border border-transparent hover:bg-zinc-800'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                        isSelected ? 'border-yellow-500 bg-yellow-500' : 'border-zinc-600'
                      }`}>
                        {isSelected && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-white truncate">{video.title}</div>
                        <div className="text-xs text-zinc-500">
                          {video.timestamp_markers?.length || 0} markers • {video.source}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={handleCreateReel}
              disabled={creating || selectedVideos.length === 0 || !reelTitle.trim()}
              className="h-9 px-4 bg-yellow-600 hover:bg-yellow-500 text-white text-xs"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Film className="h-4 w-4 mr-2" />
              )}
              Create Highlight Reel
            </Button>
            <Button
              onClick={() => {
                setShowCreator(false);
                setSelectedVideos([]);
                setReelTitle('');
              }}
              variant="outline"
              className="h-9 px-4 border-zinc-700 text-zinc-400 text-xs"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Reels List */}
      {reels.length === 0 ? (
        <div className="text-center py-8 border-t border-zinc-800">
          <Film className="h-10 w-10 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">No highlight reels yet</p>
          <p className="text-zinc-600 text-xs mt-1">Create compilations from your video clips</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reels.map((reel) => {
            const isExpanded = expandedReel === reel.id;
            
            return (
              <div 
                key={reel.id}
                className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden"
              >
                {/* Reel Header */}
                <div 
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/30"
                  onClick={() => setExpandedReel(isExpanded ? null : reel.id)}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                      <Film className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">{reel.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <Video className="h-3 w-3" />
                          {reel.videos_included || reel.video_ids?.length} videos
                        </span>
                        <span className="flex items-center gap-1">
                          <Play className="h-3 w-3" />
                          {reel.total_clips} clips
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {reel.total_duration_formatted || formatDuration(reel.total_duration_seconds)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Share Button */}
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowShareMenu(showShareMenu === reel.id ? null : reel.id);
                        }}
                        className="p-2 hover:bg-[#02b5ff]/20 rounded text-zinc-500 hover:text-[#02b5ff]"
                        title="Share highlight reel"
                      >
                        <Share2 className="h-4 w-4" />
                      </button>
                      {showShareMenu === reel.id && (
                        <div className="absolute right-0 top-full mt-1 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 py-1 min-w-[140px]">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShare(reel, 'twitter'); }}
                            className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                          >
                            <Twitter className="h-4 w-4 text-[#1DA1F2]" /> Twitter
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShare(reel, 'facebook'); }}
                            className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                          >
                            <Facebook className="h-4 w-4 text-[#4267B2]" /> Facebook
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleShare(reel, 'copy'); }}
                            className="w-full px-3 py-2 text-left text-sm text-zinc-300 hover:bg-zinc-700 flex items-center gap-2"
                          >
                            <Link2 className="h-4 w-4" /> Copy Link
                          </button>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteReel(reel.id);
                      }}
                      className="p-2 hover:bg-red-500/20 rounded text-zinc-500 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-zinc-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-zinc-500" />
                    )}
                  </div>
                </div>

                {/* Expanded Clips */}
                {isExpanded && reel.clips && (
                  <div className="border-t border-zinc-800 p-4 bg-zinc-900/30">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {reel.clips.map((clip, idx) => (
                        <div 
                          key={clip.marker_id || idx}
                          className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded hover:bg-zinc-800 transition-colors group"
                        >
                          <span className="w-6 h-6 bg-yellow-600/30 rounded-full flex items-center justify-center text-xs text-yellow-400">
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-white flex items-center gap-1">
                              {STAT_ICONS[clip.stat_type] || STAT_ICONS.other}
                              <span className="truncate">{clip.label}</span>
                            </div>
                            <div className="text-xs text-zinc-500 truncate">
                              {clip.video_title}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePreviewClip(clip, clip.video_url);
                              }}
                              className="p-1.5 opacity-0 group-hover:opacity-100 hover:bg-[#02b5ff]/20 rounded text-zinc-400 hover:text-[#02b5ff] transition-all"
                              title="Preview clip"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-xs font-mono text-[#02b5ff]">
                              {formatDuration(clip.time_seconds)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Stats Summary */}
                    {reel.stat_summary && Object.keys(reel.stat_summary).length > 0 && (
                      <div className="mt-4 pt-4 border-t border-zinc-800">
                        <div className="text-xs text-zinc-500 uppercase mb-2">Clip Summary</div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(reel.stat_summary).map(([stat, count]) => (
                            <span 
                              key={stat}
                              className="px-2 py-1 bg-zinc-800 rounded text-sm"
                            >
                              {STAT_ICONS[stat] || '📝'} {stat.replace('_', ' ')}: {count}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Clip Preview Modal */}
      {previewClip && (
        <div 
          className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewClip(null)}
        >
          <div 
            className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-3xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{STAT_ICONS[previewClip.stat_type] || STAT_ICONS.other}</span>
                <div>
                  <h3 className="text-white font-bold">{previewClip.label}</h3>
                  <p className="text-xs text-zinc-500">{previewClip.video_title}</p>
                </div>
              </div>
              <button
                onClick={() => setPreviewClip(null)}
                className="p-2 hover:bg-zinc-800 rounded-full text-zinc-400 hover:text-white"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>

            {/* Video Player */}
            <div className="relative bg-black aspect-video">
              {previewClip.videoUrl ? (
                <video
                  ref={videoRef}
                  src={previewClip.videoUrl}
                  className="w-full h-full object-contain"
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={() => setIsPlaying(false)}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-zinc-500">
                  <Film className="h-16 w-16" />
                </div>
              )}
              
              {/* Play/Pause Overlay */}
              <div 
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={handlePlayPause}
              >
                {!isPlaying && (
                  <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                    <Play className="h-8 w-8 text-white ml-1" />
                  </div>
                )}
              </div>
            </div>

            {/* Controls */}
            <div className="p-4 bg-zinc-800/50">
              {/* Progress Bar */}
              <div 
                className="h-1 bg-zinc-700 rounded-full mb-3 cursor-pointer"
                onClick={handleSeek}
              >
                <div 
                  className="h-full bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full transition-all"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>
              
              {/* Control Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleSkip(-5)}
                    className="p-2 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                    title="Back 5 seconds"
                  >
                    <SkipBack className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handlePlayPause}
                    className="p-3 bg-yellow-600 hover:bg-yellow-500 rounded-full text-white"
                  >
                    {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5 ml-0.5" />}
                  </button>
                  <button
                    onClick={() => handleSkip(5)}
                    className="p-2 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                    title="Forward 5 seconds"
                  >
                    <SkipForward className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-2 hover:bg-zinc-700 rounded text-zinc-400 hover:text-white"
                  >
                    {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                  </button>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <span className="font-mono">{formatDuration(currentTime)}</span>
                  <span>/</span>
                  <span className="font-mono">{formatDuration(duration)}</span>
                </div>
              </div>
            </div>

            {/* Clip Info */}
            <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
              <div className="text-sm text-zinc-400">
                Clip starts at <span className="text-[#02b5ff] font-mono">{formatDuration(previewClip.time_seconds)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-400"
                  onClick={() => setPreviewClip(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
